import { Link } from "react-router-dom";
import { Star, StarOff } from "lucide-react";
import type { Forum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CategoryBadge } from "@/components/ui/badge";
import { TrendBadge } from "@/components/TrendBadge";
import { VariationBadge } from "@/components/PriceTag";
import { formatNumber, formatScore, fromNowFR } from "@/lib/utils";

type Props = {
  forum: Forum;
  isFollowed: boolean;
  onToggleFollow: () => void;
  windowLabel: "24h" | "7d";
  threadsCur: number;
  threadsPrev: number;
  postsCur: number;
  postsPrev: number;
  /** Numbered position in the list (optional). */
  rank?: number;
};

/**
 * ForumCard — mobile representation of a forum row.
 *
 * Layout :
 *   ┌─────────────────────────────────────────────┐
 *   │ #1   TITLE                       [Suivre]   │   header line
 *   │       slug · CategoryBadge                  │
 *   ├─────────────────────────────────────────────┤
 *   │  Sujets        Activité       Var.          │
 *   │  142 +35%      820 +12%       +1,24%        │   metric grid
 *   ├─────────────────────────────────────────────┤
 *   │  Score 87,4 · Dernier post il y a 2h        │   footer (subtle)
 *   └─────────────────────────────────────────────┘
 *
 * The title is fully tappable (links to /forums/:id/threads). The follow
 * button is icon-only with aria-label, sized to meet the 44 px touch
 * target. Metrics are arranged in a 3-column grid so they align nicely
 * regardless of value length.
 */
export function ForumCard({
  forum,
  isFollowed,
  onToggleFollow,
  windowLabel,
  threadsCur,
  threadsPrev,
  postsCur,
  postsPrev,
  rank,
}: Props) {
  return (
    <article className="rounded-lg border border-border bg-surface p-4 flex flex-col gap-3 transition-colors hover:border-border-strong">
      {/* ── Header : rank + title + follow ── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/forums/${forum.id}/threads`}
            className="block font-semibold text-foreground hover:text-primary leading-tight"
          >
            {rank != null && (
              <span className="text-foreground-subtle text-xs mr-1.5">
                #{rank}
              </span>
            )}
            {forum.title || forum.slug}
          </Link>
          <div className="flex items-center gap-2 mt-1">
            <CategoryBadge category={forum.category ?? "unknown"} />
            {forum.slug !== forum.title && (
              <span className="text-xs text-foreground-subtle">{forum.slug}</span>
            )}
          </div>
        </div>
        <Button
          size="icon"
          variant={isFollowed ? "default" : "outline"}
          onClick={onToggleFollow}
          aria-label={isFollowed ? "Ne plus suivre ce forum" : "Suivre ce forum"}
          className="shrink-0"
        >
          {isFollowed ? <Star className="size-4" /> : <StarOff className="size-4" />}
        </Button>
      </div>

      {/* ── Metric grid : 3 columns ── */}
      <div className="grid grid-cols-3 gap-2 -mx-1">
        <Metric
          label={`Sujets ${windowLabel}`}
          value={formatNumber(threadsCur)}
          trend={<TrendBadge current={threadsCur} previous={threadsPrev} />}
        />
        <Metric
          label={`Activité ${windowLabel}`}
          value={formatNumber(postsCur)}
          trend={<TrendBadge current={postsCur} previous={postsPrev} />}
        />
        <Metric
          label="Var. cours"
          value={<VariationBadge value={forum.last_variation_pct} />}
        />
      </div>

      {/* ── Footer : score + last reply ── */}
      <div className="flex items-center gap-3 text-xs text-foreground-muted border-t border-border pt-2">
        <span>
          Score{" "}
          <span className="text-foreground font-medium tabular-nums">
            {formatScore(forum.active_score)}
          </span>
        </span>
        <span aria-hidden>·</span>
        <span className="truncate">
          Dernier post {fromNowFR(forum.last_reply_at)}
        </span>
      </div>
    </article>
  );
}

function Metric({
  label,
  value,
  trend,
}: {
  label: string;
  value: React.ReactNode;
  trend?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 px-1 min-w-0">
      <span className="text-[10px] uppercase tracking-wide text-foreground-subtle truncate">
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-base font-semibold tabular-nums text-foreground truncate">
          {value}
        </span>
      </div>
      {trend && <div className="-mt-0.5">{trend}</div>}
    </div>
  );
}
